/**
 * 登入頁（P1-3；P1-4b 改用 vendor 嘅 `form-text-field` ＋ `ui-kit` 樣式）
 * 四方同一入口（供應商／物流／職員／管理員）。
 * ⚠️ 角色唔喺登入頁揀：由伺服器憑帳號決定（唔可以畀用戶自選角色）。
 *
 * ⭐ P1.5：入嘅唔止帳號 —— **設定完 email 之後就用 email 登入**（同一個欄位收兩種）。
 *   第一次登入（未設定 email）→ 伺服器回 `needsSetup: true` → 前端即刻轉去 `/setup`
 *   （設定 email ＋ 新密碼；唔准繼續用預設密碼）。
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { api, type Me } from '../lib/api';

export default function Login({ onDone }: { onDone: (user: Me) => void }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState<string | null>('');
  const [password, setPassword] = useState<string | null>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.login((username ?? '').trim(), password ?? '');
      // ⭐ 未設定 email → 直接入「首次設定」（`App` 見到 `needsSetup` 亦會再擋一次）
      onDone(res.user);
    } catch {
      // 伺服器只回「帳號或密碼不正確」→ 前端亦唔區分（防用戶列舉）
      setError(t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...card, padding: '16px' }}>
      <h2 style={{ margin: '0 0 12px', fontSize: '17px', fontWeight: 800 }}>{t('login.title')}</h2>
      <form onSubmit={submit}>
        <div style={{ marginBottom: '12px' }}>
          <FormTextField
            id="f-username"
            labelKey="login.identifier"
            value={username}
            onChange={setUsername}
          />
          <p style={hintStyle}>{t('login.identifierHint')}</p>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <FormTextField
            id="f-password"
            labelKey="login.password"
            value={password}
            onChange={setPassword}
            type="password"
          />
        </div>
        {error && (
          <p style={errText} role="alert">
            {error}
          </p>
        )}
        <button type="submit" style={btnPrimary} disabled={busy}>
          {busy ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
    </section>
  );
}
